import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DICT, Lang, Dict } from "../i18n/dict";

// Feature flag: when false, EN UI is disabled (toggle hidden, setLang noop,
// blog index filtered to UK only, post template won't sync to EN URLs).
// EN dict + EN MDX files are preserved on disk so this can be flipped back on.
export const EN_ENABLED = false;

export interface PageTranslation {
  otherLangPath?: string | null;
}

interface SiteState {
  lang: Lang;
  setLang: (l: Lang) => void;
  dark: boolean;
  setDark: (d: boolean) => void;
  toggleDark: () => void;
  mounted: boolean;
  t: Dict;
  pageTranslation: PageTranslation;
  setPageTranslation: (p: PageTranslation) => void;
}

const SiteCtx = createContext<SiteState | null>(null);

const LS_LANG = "site:lang";
const LS_DARK = "site:dark";

export const SiteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // SSR-safe defaults. Real theme is applied to <html> by the inline blocking
  // script in gatsby-ssr.js BEFORE first paint, so CSS variables resolve to
  // the correct theme even though React state is still `false` here.
  const [lang, setLangState] = useState<Lang>("uk");
  const [dark, setDarkState] = useState<boolean>(false);
  const [mounted, setMounted] = useState<boolean>(false);
  const [pageTranslation, setPageTranslation] = useState<PageTranslation>({});

  // After hydration: sync React state to whatever the inline script wrote on
  // <html>. Also flip the `theme-transitions` switch so the body transition
  // only runs on user toggle (not on first paint).
  useEffect(() => {
    try {
      const themeAttr = document.documentElement.getAttribute("data-theme");
      if (themeAttr === "dark") setDarkState(true);
      const langAttr = document.documentElement.getAttribute("lang") as Lang | null;
      if (EN_ENABLED && (langAttr === "uk" || langAttr === "en")) {
        setLangState(langAttr);
      }
    } catch (_) { /* ignore */ }
    // Defer to next frame so initial paint completes before transitions enable.
    const id = window.requestAnimationFrame(() => {
      document.documentElement.classList.add("theme-transitions");
      setMounted(true);
    });
    return () => window.cancelAnimationFrame(id);
  }, []);

  // On user toggle (post-mount only): write to DOM + localStorage. Skipping
  // pre-mount runs is critical — the inline script already set the attribute,
  // and re-writing here would clobber it during hydration.
  useEffect(() => {
    if (!mounted) return;
    document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
    try { localStorage.setItem(LS_DARK, dark ? "1" : "0"); } catch (_) { /* ignore */ }
  }, [dark, mounted]);

  useEffect(() => {
    if (!mounted) return;
    document.documentElement.setAttribute("lang", lang);
    if (EN_ENABLED) {
      try { localStorage.setItem(LS_LANG, lang); } catch (_) { /* ignore */ }
    }
  }, [lang, mounted]);

  const setLang = useCallback((l: Lang) => {
    if (!EN_ENABLED) return;
    setLangState(l);
  }, []);

  const setDark = useCallback((d: boolean) => {
    setDarkState(d);
  }, []);

  const toggleDark = useCallback(() => setDarkState((d) => !d), []);

  const value = useMemo<SiteState>(
    () => ({
      lang, setLang, dark, setDark, toggleDark, mounted,
      t: DICT[lang],
      pageTranslation,
      setPageTranslation,
    }),
    [lang, dark, mounted, setLang, setDark, toggleDark, pageTranslation]
  );

  return <SiteCtx.Provider value={value}>{children}</SiteCtx.Provider>;
};

export function useSite(): SiteState {
  const ctx = useContext(SiteCtx);
  if (!ctx) {
    return {
      lang: "uk",
      setLang: () => {},
      dark: false,
      setDark: () => {},
      toggleDark: () => {},
      mounted: false,
      t: DICT.uk,
      pageTranslation: {},
      setPageTranslation: () => {},
    };
  }
  return ctx;
}

export function useSetPageTranslation(other: string | null | undefined) {
  const { setPageTranslation } = useSite();
  useEffect(() => {
    setPageTranslation({ otherLangPath: other ?? undefined });
    return () => setPageTranslation({});
  }, [other, setPageTranslation]);
}